import { createAction, props } from '@ngrx/store';

import {
  Application,
  ApplicationConfig,
  ApplicationStep,
  Client,
  Feedback,
  Proposal,
  Quote,
} from '@lockton-apps/shared/interfaces';

import { LsosRenewalsApplicationActionTypes } from './types';

export const init = createAction(LsosRenewalsApplicationActionTypes.Init);

export const loadQuotes = createAction(
  LsosRenewalsApplicationActionTypes.LoadQuotes,
  props<{ clientId: number }>()
);

export const loadQuotesSuccess = createAction(
  LsosRenewalsApplicationActionTypes.LoadQuotesSuccess,
  props<{ quotes: Quote[] }>()
);

export const loadEmptyQuotesSuccess = createAction(
  LsosRenewalsApplicationActionTypes.LoadEmptyQuotes
);

export const loadQuotesFailure = createAction(
  LsosRenewalsApplicationActionTypes.LoadQuotesFailure,
  props<{ error: any }>()
);

export const readConfig = createAction(
  LsosRenewalsApplicationActionTypes.ReadConfig
);

export const readConfigSuccess = createAction(
  LsosRenewalsApplicationActionTypes.ReadConfigSuccess,
  props<{ config: ApplicationConfig }>()
);

export const readConfigFailure = createAction(
  LsosRenewalsApplicationActionTypes.ReadConfigFailure
);

export const buildApplication = createAction(
  LsosRenewalsApplicationActionTypes.BuildApplication
);

export const addProposal = createAction(
  LsosRenewalsApplicationActionTypes.AddProposal,
  props<{ application: Application }>()
);

export const addProposalSuccess = createAction(
  LsosRenewalsApplicationActionTypes.AddProposalSuccess,
  props<{ proposal: Proposal }>()
);

export const addProposalFailure = createAction(
  LsosRenewalsApplicationActionTypes.AddProposalFailure
);

export const loadProposal = createAction(
  LsosRenewalsApplicationActionTypes.LoadProposal,
  props<{ quoteId: number }>()
);

export const loadProposalSuccess = createAction(
  LsosRenewalsApplicationActionTypes.LoadProposalSuccess,
  props<{ proposal: Proposal }>()
);

export const loadProposalFailure = createAction(
  LsosRenewalsApplicationActionTypes.LoadProposalFailure
);

export const continueProposal = createAction(
  LsosRenewalsApplicationActionTypes.ContinueProposal
);

export const continueProposalWithoutRebuild = createAction(
  LsosRenewalsApplicationActionTypes.ContinueProposalWithoutRebuild,
  props<{ proposal: Proposal; quoteStatus: string | undefined }>()
);

export const continueProposalWithInitialBuild = createAction(
  LsosRenewalsApplicationActionTypes.ContinueProposalWithInitialBuild,
  props<{
    config: ApplicationConfig;
    proposal: Proposal;
    quoteStatus: string | undefined;
  }>()
);

export const saveInitialBuild = createAction(
  LsosRenewalsApplicationActionTypes.SaveInitialBuild,
  props<{
    proposal: Proposal;
  }>()
);

export const SaveInitialBuildSuccess = createAction(
  LsosRenewalsApplicationActionTypes.SaveInitialBuildSuccess,
  props<{
    proposal: Proposal;
  }>()
);

export const SaveInitialBuildFailure = createAction(
  LsosRenewalsApplicationActionTypes.SaveInitialBuildFailure,
  props<{
    error: any;
  }>()
);

export const loadClientContacts = createAction(
  LsosRenewalsApplicationActionTypes.LoadClientContacts,
  props<{ clientId: number }>()
);

export const loadClientAsBroker = createAction(
  LsosRenewalsApplicationActionTypes.LoadClientAsBroker,
  props<{ clientId: number }>()
);

export const loadClientAsBrokerSuccess = createAction(
  LsosRenewalsApplicationActionTypes.LoadClientAsBrokerSuccess,
  props<{ client: Client }>()
);

export const loadClientAsBrokerFailure = createAction(
  LsosRenewalsApplicationActionTypes.LoadClientAsBrokerFailure,
  props<{ error: any }>()
);

export const buildForSaveProposal = createAction(
  LsosRenewalsApplicationActionTypes.BuildForSaveProposal,
  props<{ model: any; isValid: boolean }>()
);

export const saveProposal = createAction(
  LsosRenewalsApplicationActionTypes.SaveProposal,
  props<{ application: Application; proposal: Proposal }>()
);

export const saveProposalSuccess = createAction(
  LsosRenewalsApplicationActionTypes.SaveProposalSuccess,
  props<{ proposal: Proposal }>()
);

export const saveProposalFailure = createAction(
  LsosRenewalsApplicationActionTypes.SaveProposalFailure
);

export const buildForSaveAndContinueProposal = createAction(
  LsosRenewalsApplicationActionTypes.BuildForSaveAndContinueProposal,
  props<{ model: any }>()
);

export const updateStepsState = createAction(
  LsosRenewalsApplicationActionTypes.UpdateStepsState,
  props<{ steps: ApplicationStep[] }>()
);

export const saveAndContinueProposal = createAction(
  LsosRenewalsApplicationActionTypes.SaveAndContinueProposal,
  props<{ application: Application; proposal: Proposal }>()
);

export const saveAndContinueProposalSuccess = createAction(
  LsosRenewalsApplicationActionTypes.SaveAndContinueProposalSuccess,
  props<{ proposal: Proposal }>()
);

export const saveAndContinueProposalFailure = createAction(
  LsosRenewalsApplicationActionTypes.SaveAndContinueProposalFailure
);

export const buildForSubmitProposal = createAction(
  LsosRenewalsApplicationActionTypes.BuildForSubmitProposal,
  props<{ model: any }>()
);

export const submitProposal = createAction(
  LsosRenewalsApplicationActionTypes.SubmitProposal,
  props<{ application: Application; proposal: Proposal }>()
);

export const submitProposalSuccess = createAction(
  LsosRenewalsApplicationActionTypes.SubmitProposalSuccess,
  props<{ proposal: Proposal }>()
);

export const submitProposalFailure = createAction(
  LsosRenewalsApplicationActionTypes.SubmitProposalFailure
);

export const submitFeedback = createAction(
  LsosRenewalsApplicationActionTypes.SubmitFeedback,
  props<{ feedback: Feedback }>()
);

export const submitFeedbackSuccess = createAction(
  LsosRenewalsApplicationActionTypes.SubmitFeedbackSuccess
);

export const submitFeedbackFailure = createAction(
  LsosRenewalsApplicationActionTypes.SubmitFeedbackFailure
);

export const reset = createAction(LsosRenewalsApplicationActionTypes.Reset);

export const setNewQuoteId = createAction(
  LsosRenewalsApplicationActionTypes.SetNewQuoteId,
  props<{ quoteId: number }>()
);

export const stepChange = createAction(
  LsosRenewalsApplicationActionTypes.StepChange,
  props<{ stepId: string }>()
);
